import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Volume2, Eye, EyeOff, Check, X, Globe, Home, Play, Hash, AlertCircle } from 'lucide-react';

// Numbers for training (1-10)
const NUMBERS = [
  { id: 1, nameTr: 'Bir', nameEn: 'One', color: '#8B5CF6' },
  { id: 2, nameTr: 'İki', nameEn: 'Two', color: '#3B82F6' },
  { id: 3, nameTr: 'Üç', nameEn: 'Three', color: '#06B6D4' },
  { id: 4, nameTr: 'Dört', nameEn: 'Four', color: '#10B981' },
  { id: 5, nameTr: 'Beş', nameEn: 'Five', color: '#22C55E' },
  { id: 6, nameTr: 'Altı', nameEn: 'Six', color: '#EAB308' },
  { id: 7, nameTr: 'Yedi', nameEn: 'Seven', color: '#F97316' },
  { id: 8, nameTr: 'Sekiz', nameEn: 'Eight', color: '#EF4444' },
  { id: 9, nameTr: 'Dokuz', nameEn: 'Nine', color: '#EC4899' },
  { id: 10, nameTr: 'On', nameEn: 'Ten', color: '#A855F7' }
];

// Get random number
const getRandomNumber = () => {
  return NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
};

// Text-to-Speech helper
const speak = (text, lang = 'tr-TR') => {
  return new Promise((resolve) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9;
    utterance.pitch = 1.2;
    utterance.onend = resolve;
    utterance.onerror = resolve;
    window.speechSynthesis.speak(utterance);
  });
};

// Language Selector
const LanguageSelector = ({ language, onSelect }) => (
  <div className="flex items-center gap-2">
    <Globe className="w-4 h-4 text-slate-400" />
    <div className="flex rounded-full overflow-hidden border border-slate-700">
      <button
        onClick={() => onSelect('tr')}
        className={`px-3 py-1 text-xs transition-colors ${
          language === 'tr' ? 'bg-amber-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
        }`}
      >
        TR
      </button>
      <button
        onClick={() => onSelect('en')}
        className={`px-3 py-1 text-xs transition-colors ${
          language === 'en' ? 'bg-amber-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
        }`}
      >
        EN
      </button>
    </div>
  </div>
);

// Home Button
const HomeButton = ({ onHome }) => (
  <button
    onClick={onHome}
    className="fixed top-6 right-6 z-50 glass-panel p-3 text-slate-400 hover:text-white transition-all flex items-center gap-2"
  >
    <Home className="w-5 h-5" />
    <span className="text-sm">Home</span>
  </button>
);

// Instructions Screen
const InstructionsScreen = ({ language, onContinue, onBack, onHome }) => {
  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <motion.div
          className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center border border-amber-500/30"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <EyeOff className="w-12 h-12 text-amber-400" />
        </motion.div>
        
        <h1 className="text-3xl font-heading text-slate-100">
          {language === 'tr' ? 'Teste Hazırlan' : 'Get Ready for Test'}
        </h1>
        
        {/* Important Notice */}
        <div className="glass-panel p-6 space-y-4 text-left border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-3 text-amber-400">
            <AlertCircle className="w-6 h-6 flex-shrink-0" />
            <h3 className="text-lg font-medium">
              {language === 'tr' ? 'Önemli Bilgilendirme' : 'Important Notice'}
            </h3>
          </div>
          
          <div className="space-y-4 text-slate-300">
            <p>
              {language === 'tr' 
                ? '🎭 Şimdi gözlerini ışık geçirmeyen koyu renkli bir göz bandı ile kapat.'
                : '🎭 Now close your eyes with a dark, light-blocking eye mask.'}
            </p>
            <p>
              {language === 'tr'
                ? '🔢 Sana rakamları göstereceğiz ve ekrana yansıtacağız. Toplamda 5 farklı rakamı rastgele seçeceğiz.'
                : '🔢 We will show you numbers on the screen. We will randomly select 5 different numbers.'}
            </p>
            <p>
              {language === 'tr'
                ? '⏱️ Her rakam 15 saniye ekranda kalacak ve sürenin sonunda sesli şekilde duyacaksın.'
                : '⏱️ Each number will stay on screen for 15 seconds and you will hear it announced at the end.'}
            </p>
            <p className="text-amber-400 font-medium">
              {language === 'tr'
                ? '⚠️ Biz "Test bitti, gözlerini açabilirsin" diyene kadar gözlerini açma!'
                : '⚠️ Do not open your eyes until we say "Test is over, you can open your eyes"!'}
            </p>
          </div>
        </div>
        
        {/* Blindfold Note */}
        <div className="glass-panel p-4 text-sm text-slate-400 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
            🕶️
          </div>
          <p className="text-left">
            {language === 'tr'
              ? 'Rakam testinde ışık geçirmeyen koyu renkli bir göz bandı kullanılmalıdır.'
              : 'A dark, light-blocking blindfold should be used for the number test.'}
          </p>
        </div>
        
        <button
          onClick={onContinue}
          className="w-full max-w-sm mx-auto rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 flex items-center justify-center gap-2"
        >
          <Play className="w-4 h-4" />
          {language === 'tr' ? 'Göz Bandımı Taktım, Başla' : 'I Put On My Blindfold, Start'}
        </button>
      </div>
    </motion.div>
  );
};

// Intro Screen
const IntroScreen = ({ language, onLanguageChange, onStart, onBack, onHome }) => {
  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-40">
        <LanguageSelector language={language} onSelect={onLanguageChange} />
      </div>
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <div className="space-y-4">
          <motion.div
            className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center border border-amber-500/30"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Hash className="w-12 h-12 text-amber-400" />
          </motion.div>
          
          <h1 className="text-3xl font-heading text-slate-100">
            {language === 'tr' ? 'Rakam Algılama Eğitimi' : 'Number Perception Training'}
          </h1>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            {language === 'tr' 
              ? 'Gözlerinizi kapatın ve rakamları zihninizle görmeye çalışın.'
              : 'Close your eyes and try to see numbers with your mind.'}
          </p>
        </div>
        
        {/* Number Preview */}
        <div className="flex flex-wrap justify-center gap-3">
          {NUMBERS.slice(0, 5).map((num) => (
            <div
              key={num.id}
              className="w-12 h-12 rounded-xl border border-white/10 shadow-lg flex items-center justify-center text-xl font-bold text-white"
              style={{ backgroundColor: num.color }}
            >
              {num.id}
            </div>
          ))}
        </div>
        
        {/* How it works */}
        <div className="glass-panel p-6 space-y-4 text-left">
          <h3 className="text-amber-400 text-sm font-medium">
            {language === 'tr' ? 'Nasıl Çalışır?' : 'How It Works?'}
          </h3>
          <ol className="space-y-3 text-sm text-slate-300">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs flex-shrink-0">1</span>
              <span>{language === 'tr' ? 'Koyu renkli göz bandı ile gözlerinizi kapatın' : 'Close your eyes with a dark blindfold'}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs flex-shrink-0">2</span>
              <span>{language === 'tr' ? '5 farklı rakam ekrana yansıtılacak, her biri 15 saniye' : '5 different numbers will be displayed, each for 15 seconds'}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs flex-shrink-0">3</span>
              <span>{language === 'tr' ? 'Süre dolduğunda rakam sesli olarak söylenecek' : 'When time is up, the number will be announced'}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs flex-shrink-0">4</span>
              <span>{language === 'tr' ? 'Test bitince kaç tane doğru bildiğinizi işaretleyeceksiniz' : 'After the test, you will mark how many you got right'}</span>
            </li>
          </ol>
        </div>
        
        <button
          onClick={onStart}
          className="w-full max-w-sm mx-auto rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 flex items-center justify-center gap-2"
        >
          <Play className="w-4 h-4" />
          {language === 'tr' ? 'Eğitime Başla' : 'Start Training'}
        </button>
      </div>
    </motion.div>
  );
};

// Test Phase
const TestPhase = ({ language, round, totalRounds, shownNumbers, onComplete, onExit, onHome }) => {
  const [currentNumber, setCurrentNumber] = useState(null);
  const [countdown, setCountdown] = useState(15);
  const [phase, setPhase] = useState('focus');
  
  const langCode = language === 'tr' ? 'tr-TR' : 'en-US';
  
  useEffect(() => {
    const newNumber = getRandomNumber();
    setCurrentNumber(newNumber);
    setCountdown(15);
    setPhase('focus');
  }, [round]);
  
  useEffect(() => {
    if (phase !== 'focus' || !currentNumber) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          revealNumber();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [phase, currentNumber]);
  
  const revealNumber = async () => {
    setPhase('reveal');
    const numberName = language === 'tr' ? currentNumber.nameTr : currentNumber.nameEn;
    await speak(numberName, langCode);
    
    setTimeout(() => {
      const newShownNumbers = [...shownNumbers, currentNumber];
      if (round >= totalRounds) {
        speak(language === 'tr' ? 'Test bitti. Gözlerini açabilirsin.' : 'Test is over. You can open your eyes.', langCode);
        onComplete(newShownNumbers);
      } else {
        onComplete(null, round + 1, newShownNumbers);
      }
    }, 2000);
  };
  
  if (!currentNumber) return null;
  
  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center"
      style={{ backgroundColor: currentNumber.color }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onExit}
        className="fixed top-6 left-6 z-50 p-3 rounded-xl bg-black/20 text-white/60 hover:text-white transition-all"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50">
        <div className="px-4 py-2 rounded-xl bg-black/20 text-sm text-white">
          {language === 'tr' ? 'Rakam' : 'Number'} {round} / {totalRounds}
        </div>
      </div>
      
      <button
        onClick={onHome}
        className="fixed top-6 right-6 z-50 p-3 rounded-xl bg-black/20 text-white/60 hover:text-white transition-all flex items-center gap-2"
      >
        <Home className="w-5 h-5" />
        <span className="text-sm">Home</span>
      </button>
      
      {/* Large number display - for those who can see through blindfold or debugging */}
      <div className="text-[200px] font-bold text-white/10 select-none">
        {currentNumber.id}
      </div>
      
      {phase === 'reveal' && (
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute inset-0 flex items-center justify-center bg-black/50"
        >
          <div className="text-center">
            <Volume2 className="w-16 h-16 mx-auto mb-4 text-white" />
            <h2 className="text-8xl font-heading text-white">{currentNumber.id}</h2>
            <p className="text-3xl text-white/80 mt-4">
              {language === 'tr' ? currentNumber.nameTr : currentNumber.nameEn}
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Score Selection Screen
const ScoreSelectionScreen = ({ language, totalRounds, shownNumbers, onSubmit, onBack, onHome }) => {
  const [selectedScore, setSelectedScore] = useState(null);
  
  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <div className="space-y-4">
          <motion.div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 flex items-center justify-center border border-green-500/30">
            <Check className="w-10 h-10 text-green-400" />
          </motion.div>
          
          <h1 className="text-3xl font-heading text-slate-100">
            {language === 'tr' ? 'Test Tamamlandı!' : 'Test Complete!'}
          </h1>
          <p className="text-slate-400">
            {language === 'tr' ? 'Gözlerini açabilirsin.' : 'You can open your eyes now.'}
          </p>
        </div>
        
        {/* Shown numbers */}
        <div className="glass-panel p-6 space-y-4">
          <h3 className="text-amber-400 text-sm font-medium text-left">
            {language === 'tr' ? 'Gösterilen Rakamlar' : 'Shown Numbers'}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            {shownNumbers.map((num, index) => (
              <div key={`shown-${num.id}-${index}`} className="flex flex-col items-center gap-2">
                <div
                  className="w-14 h-14 rounded-xl border border-white/10 shadow-lg flex items-center justify-center text-2xl font-bold text-white"
                  style={{ backgroundColor: num.color }}
                >
                  {num.id}
                </div>
                <span className="text-xs text-slate-400">
                  {language === 'tr' ? num.nameTr : num.nameEn}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Score selection */}
        <div className="glass-panel p-6 space-y-4">
          <h3 className="text-amber-400 text-sm font-medium">
            {language === 'tr' ? 'Kaç tanesini doğru bildin?' : 'How many did you get right?'}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            {[0, 1, 2, 3, 4, 5].map((score) => (
              <button
                key={score}
                onClick={() => setSelectedScore(score)}
                className={`w-14 h-14 rounded-xl border-2 font-bold text-xl transition-all ${
                  selectedScore === score 
                    ? 'bg-amber-500 border-amber-400 text-white scale-110' 
                    : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-amber-500'
                }`}
              >
                {score}
              </button>
            ))}
          </div>
        </div>
        
        <button
          onClick={() => selectedScore !== null && onSubmit(selectedScore)}
          disabled={selectedScore === null}
          className="w-full max-w-sm mx-auto rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {language === 'tr' ? 'Sonucu Kaydet' : 'Save Result'}
        </button>
      </div>
    </motion.div>
  );
};

// Results Screen
const ResultsScreen = ({ language, score, totalRounds, shownNumbers, onRestart, onBack, onHome }) => {
  const percentage = Math.round((score / totalRounds) * 100);
  
  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('zenermind_number_history') || '[]');
    history.push({
      date: new Date().toISOString(),
      score,
      total: totalRounds,
      percentage,
      numbers: shownNumbers.map(n => n.id)
    });
    localStorage.setItem('zenermind_number_history', JSON.stringify(history.slice(-50)));
  }, [score, totalRounds, percentage, shownNumbers]);
  
  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <h1 className="text-3xl font-heading text-slate-100">
          {language === 'tr' ? 'Sonuçların' : 'Your Results'}
        </h1>
        
        <div className="glass-panel p-8 space-y-4">
          <div className="text-6xl font-heading text-amber-400">
            {score} / {totalRounds}
          </div>
          <div className="text-2xl text-slate-300">%{percentage}</div>
          <p className="text-slate-500 text-sm">
            {percentage >= 80 && (language === 'tr' ? 'Mükemmel!' : 'Excellent!')}
            {percentage >= 60 && percentage < 80 && (language === 'tr' ? 'Çok iyi!' : 'Very good!')}
            {percentage >= 40 && percentage < 60 && (language === 'tr' ? 'İyi!' : 'Good!')}
            {percentage < 40 && (language === 'tr' ? 'Pratik yap!' : 'Practice more!')}
          </p>
        </div>
        
        <div className="glass-panel p-6 space-y-4">
          <h3 className="text-amber-400 text-sm font-medium text-left">
            {language === 'tr' ? 'Gösterilen Rakamlar' : 'Shown Numbers'}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            {shownNumbers.map((num, index) => (
              <div key={`shown-${num.id}-${index}`} className="flex flex-col items-center gap-2">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold text-white"
                  style={{ backgroundColor: num.color }}
                >
                  {num.id}
                </div>
                <span className="text-xs text-slate-400">
                  {language === 'tr' ? num.nameTr : num.nameEn}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex gap-4 justify-center">
          <button
            onClick={onRestart}
            className="rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-8 py-4 text-sm font-medium text-white border border-amber-500/30"
          >
            {language === 'tr' ? 'Tekrar Dene' : 'Try Again'}
          </button>
          <button
            onClick={onHome}
            className="rounded-full bg-slate-800/50 px-8 py-4 text-sm font-medium text-slate-300 border border-slate-700/50 flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Home
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
export const NumberTraining = ({ onBack, onHome }) => {
  const [phase, setPhase] = useState('intro');
  const [language, setLanguage] = useState('tr');
  const [currentRound, setCurrentRound] = useState(1);
  const [shownNumbers, setShownNumbers] = useState([]);
  const [score, setScore] = useState(0);
  const totalRounds = 5;
  
  const handleStart = () => setPhase('instructions');
  
  const handleInstructionsContinue = () => {
    setPhase('test');
    setCurrentRound(1);
    setShownNumbers([]);
  };
  
  const handleTestComplete = (finalNumbers, nextRound, newNumbers) => {
    if (finalNumbers) {
      setShownNumbers(finalNumbers);
      setPhase('score');
    } else if (nextRound && newNumbers) {
      setCurrentRound(nextRound);
      setShownNumbers(newNumbers);
    }
  };
  
  const handleScoreSubmit = (selectedScore) => {
    setScore(selectedScore);
    setPhase('results');
  };
  
  const handleRestart = () => {
    setPhase('intro');
    setCurrentRound(1);
    setShownNumbers([]);
    setScore(0);
  };
  
  const handleHomeClick = () => onHome ? onHome() : onBack && onBack();
  
  return (
    <AnimatePresence mode="wait">
      {phase === 'intro' && (
        <IntroScreen
          key="intro"
          language={language}
          onLanguageChange={setLanguage}
          onStart={handleStart}
          onBack={onBack}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'instructions' && (
        <InstructionsScreen
          key="instructions"
          language={language}
          onContinue={handleInstructionsContinue}
          onBack={() => setPhase('intro')}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'test' && (
        <TestPhase
          key={`test-${currentRound}`}
          language={language}
          round={currentRound}
          totalRounds={totalRounds}
          shownNumbers={shownNumbers}
          onComplete={handleTestComplete}
          onExit={() => setPhase('intro')}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'score' && (
        <ScoreSelectionScreen
          key="score"
          language={language}
          totalRounds={totalRounds}
          shownNumbers={shownNumbers}
          onSubmit={handleScoreSubmit}
          onBack={() => setPhase('intro')}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'results' && (
        <ResultsScreen
          key="results"
          language={language}
          score={score}
          totalRounds={totalRounds}
          shownNumbers={shownNumbers}
          onRestart={handleRestart}
          onBack={onBack}
          onHome={handleHomeClick}
        />
      )}
    </AnimatePresence>
  );
};

export default NumberTraining;
